import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Input, Price, PriceInput, Select, Tabs } from '@artosera/ui';
import { ETAPES_AFFAIRE, ETAPES_OUVERTES, formatDate, type EtapeAffaire } from '@artosera/shared';
import {
  pipelineApi,
  useAffaires,
  useActionPipeline,
  type AffairePresentee,
  type FiltresAffaires,
} from '../../api/pipeline.js';
import { useEquipe } from '../../api/equipe.js';
import { useArtistes } from '../../api/artistes.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useMenuMobile } from '../shell/hooks.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { NouvelleAffaireDialog } from './NouvelleAffaireDialog.js';
import { Prevision } from './Prevision.js';
import { MenuDeplacement, PerteDialog } from './Deplacement.js';
import { AffairePanneau } from './AffairePanneau.js';
import styles from './Pipeline.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

export function PipelinePage() {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const [params, setParams] = useSearchParams();
  const mobile = useMenuMobile();
  const [nouvelle, setNouvelle] = useState(false);
  const [perte, setPerte] = useState<AffairePresentee | null>(null);
  const [filtres, setFiltres] = useState<FiltresAffaires>({});
  const [colonneMobile, setColonneMobile] = useState<string>('interet');
  const [repliees, setRepliees] = useState<EtapeAffaire[]>(['gagnee', 'perdue']);
  const lire = galerieALaction(galerie, 'pipeline:read');
  const ecrire = galerieALaction(galerie, 'pipeline:write');
  const voitMontants =
    !galerieAModule(galerie, 'roles-fins') ||
    galerieALaction(galerie, 'artworks:read-artist-price');
  const liste = useAffaires(
    galerieSlug,
    { ...filtres, montantMin: voitMontants ? filtres.montantMin : undefined },
    lire,
  );
  const equipe = useEquipe(galerieSlug, { enabled: galerieALaction(galerie, 'memberships:read') });
  const artistes = useArtistes(
    galerieSlug,
    { parPage: 200 },
    { enabled: galerieALaction(galerie, 'artists:read') },
  );
  const deplacement = useActionPipeline(
    galerieSlug,
    ({ affaire, vers }: { affaire: AffairePresentee; vers: EtapeAffaire }, appel) =>
      pipelineApi.deplacer(galerieSlug, affaire.id, { vers, version: affaire.version }, appel),
  );
  const montants =
    liste.data &&
    Object.values(liste.data.colonnes).some((c) => c.totalPondereCentimes !== undefined);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  function deplacer(affaire: AffairePresentee, vers: EtapeAffaire) {
    if (
      !ecrire ||
      deplacement.isPending ||
      !(ETAPES_OUVERTES as readonly string[]).includes(affaire.etape) ||
      affaire.etape === vers
    )
      return;
    if (vers === 'perdue') setPerte(affaire);
    else deplacement.mutate({ affaire, vers });
  }
  function colonne(etape: EtapeAffaire, mobile = false) {
    const repliee = !mobile && repliees.includes(etape);
    const total = liste.data?.colonnes[etape];
    return (
      <section
        key={etape}
        className={styles.colonne}
        aria-label={t(`regie:pipeline.etapes.${etape}`)}
        onDragOver={(e) => {
          if (ecrire) e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          const carte = liste.data?.affaires.find(
            (a) => a.id === e.dataTransfer.getData('text/plain'),
          );
          if (carte) deplacer(carte, etape);
        }}
      >
        <header>
          <h2>
            {t(`regie:pipeline.etapes.${etape}`)} <span>{total?.nombre ?? 0}</span>
          </h2>
          {total?.totalPondereCentimes !== undefined ? (
            <Price cents={total.totalPondereCentimes} currency="EUR" lang={langue} />
          ) : null}
          {!mobile && ['gagnee', 'perdue'].includes(etape) ? (
            <Button
              variant="ghost"
              aria-expanded={!repliee}
              onClick={() =>
                setRepliees(repliee ? repliees.filter((c) => c !== etape) : [...repliees, etape])
              }
            >
              {t(repliee ? 'regie:pipeline.deplier' : 'regie:pipeline.replier')}
            </Button>
          ) : null}
        </header>
        {!repliee
          ? liste.data?.affaires
              .filter((a) => a.etape === etape)
              .map((a) => (
                <article
                  key={a.id}
                  className={styles.carte}
                  draggable={ecrire && (ETAPES_OUVERTES as readonly string[]).includes(a.etape)}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', a.id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  aria-label={a.numero}
                >
                  <Button
                    variant="ghost"
                    onClick={() => {
                      const suite = new URLSearchParams(params);
                      suite.set('affaire', a.id);
                      setParams(suite);
                    }}
                  >
                    {a.contactNom} · {a.numero}
                  </Button>
                  {a.vignette ? <img className={styles.vignette} src={a.vignette} alt="" /> : null}
                  {a.titre ? <p>{a.titre}</p> : null}
                  {a.montantCentimes !== undefined ? (
                    <Price cents={a.montantCentimes} currency={a.devise} lang={langue} />
                  ) : null}
                  <p>
                    {t('regie:pipeline.probabilite')} : {a.probabilite} %
                  </p>
                  {a.clotureEspereeLe ? (
                    <p>
                      {formatDate(new Date(a.clotureEspereeLe), langue)}{' '}
                      {a.enRetard ? (
                        <Badge tone="reserved">{t('regie:pipeline.enRetard')}</Badge>
                      ) : null}
                    </p>
                  ) : null}
                  <p>{a.responsableNom ?? t('regie:pipeline.sansResponsable')}</p>
                  {ecrire && (ETAPES_OUVERTES as readonly string[]).includes(a.etape) ? (
                    <MenuDeplacement
                      affaire={a}
                      onDeplacer={deplacer}
                      disabled={deplacement.isPending}
                    />
                  ) : null}
                </article>
              ))
          : null}
      </section>
    );
  }
  if (!lire) return <p role="alert">{t('regie:pipeline.accesRefuse')}</p>;
  return (
    <section className={styles.page}>
      <header className={styles.entete}>
        <h1>{t('regie:pipeline.titre')}</h1>
        <Verrouillable action="pipeline:write" ouvert={ecrire}>
          <Button onClick={() => setNouvelle(true)}>{t('regie:pipeline.nouvelle')}</Button>
        </Verrouillable>
      </header>
      {montants ? <Prevision /> : null}
      <div className={styles.filtres} role="search" aria-label={t('regie:pipeline.filtres')}>
        <Field htmlFor="pipeline-responsable-filtre" label={t('regie:pipeline.responsable')}>
          <Select
            id="pipeline-responsable-filtre"
            value={filtres.responsableId ?? ''}
            onValueChange={(v) => setFiltres({ ...filtres, responsableId: v || undefined })}
            options={[
              { value: '', label: t('regie:pipeline.tous') },
              ...(equipe.data?.membres.map((m) => ({ value: m.compteId, label: m.nom })) ?? []),
            ]}
          />
        </Field>
        <Field htmlFor="pipeline-artiste" label={t('regie:pipeline.artiste')}>
          <Select
            id="pipeline-artiste"
            value={filtres.artisteId ?? ''}
            onValueChange={(v) => setFiltres({ ...filtres, artisteId: v || undefined })}
            options={[
              { value: '', label: t('regie:pipeline.tous') },
              ...(artistes.data?.artistes.map((a) => ({ value: a.id, label: a.nom })) ?? []),
            ]}
          />
        </Field>
        {(['clotureDu', 'clotureAu'] as const).map((cle) => (
          <Field key={cle} htmlFor={`pipeline-${cle}`} label={t(`regie:pipeline.${cle}`)}>
            <Input
              id={`pipeline-${cle}`}
              type="date"
              value={filtres[cle] ?? ''}
              onChange={(e) => setFiltres({ ...filtres, [cle]: e.target.value || undefined })}
            />
          </Field>
        ))}
        {montants ? (
          <Field htmlFor="pipeline-minimum" label={t('regie:pipeline.montantMin')}>
            <PriceInput
              optional
              id="pipeline-minimum"
              cents={filtres.montantMin ?? null}
              lang={langue}
              onCentsChange={(v) => setFiltres({ ...filtres, montantMin: v ?? undefined })}
            />
          </Field>
        ) : null}
      </div>
      {liste.isLoading ? <p role="status">{t('commun:chargement')}</p> : null}
      {liste.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
      {deplacement.isError ? (
        <p role="alert">
          {estApiError(deplacement.error) && deplacement.error.status === 409
            ? t('regie:pipeline.conflit')
            : t('commun:erreurReseau')}
        </p>
      ) : null}
      {mobile ? (
        <div className={styles.mobile}>
          <Tabs
            value={colonneMobile}
            onValueChange={setColonneMobile}
            tabs={ETAPES_AFFAIRE.map((e) => ({
              value: e,
              label: t(`regie:pipeline.etapes.${e}`),
              content: colonne(e, true),
            }))}
          />
        </div>
      ) : (
        <div className={styles.colonnes}>{ETAPES_AFFAIRE.map((e) => colonne(e))}</div>
      )}
      {nouvelle ? <NouvelleAffaireDialog open onOpenChange={setNouvelle} /> : null}
      {params.get('affaire') ? (
        <AffairePanneau
          id={params.get('affaire')!}
          onFermer={() => {
            const suite = new URLSearchParams(params);
            suite.delete('affaire');
            setParams(suite);
          }}
          onDeplacer={deplacer}
        />
      ) : null}
      {perte ? <PerteDialog affaire={perte} onFermer={() => setPerte(null)} /> : null}
    </section>
  );
}
